'use client';

import { useState } from 'react';
import TestimonialCard from './TestimonialCard';
import VideoTestimonial from './VideoTestimonial';
import CaseStudyCard from './CaseStudyCard';
import FilterBar, { FilterState } from './FilterBar';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
  testimonial: string;
  stayDuration: string;
  apartmentType: string;
  date: string;
  guestType: string;
}

interface VideoTestimonialData {
  id: number;
  name: string;
  role: string;
  thumbnail: string;
  alt: string;
  duration: string;
  title: string;
}

interface CaseStudy {
  id: number;
  title: string;
  category: string;
  image: string;
  alt: string;
  challenge: string;
  solution: string;
  outcome: string;
  guestName: string;
  guestRole: string;
  guestType: string;
}

export default function GuestStoriesInteractive() {
  const [activeTab, setActiveTab] = useState<'testimonials' | 'videos' | 'case-studies'>('testimonials');
  const [filteredData, setFilteredData] = useState<{
    testimonials: Testimonial[];
    caseStudies: CaseStudy[];
  } | null>(null);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Rajesh Kumar',
    role: 'Senior Vice President',
    company: 'Tech Innovations Ltd',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_138e07571-1763295086046.png",
    alt: 'Professional Indian businessman in navy suit with confident smile in modern office',
    rating: 5,
    testimonial: 'Proximus exceeded all expectations during my 3-month project in Bangalore. The Luxos Luxury apartment felt like a five-star hotel suite with the comfort of home. The proximity to IIM Bangalore was perfect for my consulting work, and the 24/7 concierge service handled everything from laundry to restaurant reservations. The workspace setup with high-speed internet made remote meetings seamless. I\'ve recommended Proximus to my entire team.',
    stayDuration: '3 Months',
    apartmentType: 'Luxos Luxury',
    date: 'November 2024',
    guestType: 'business'
  },
  {
    id: 2,
    name: 'Sarah Mitchell',
    role: 'Marketing Director',
    company: 'Global Consulting Group',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8f896f2-1763299689286.png",
    alt: 'Professional Caucasian woman with blonde hair in business attire smiling in corporate setting',
    rating: 5,
    testimonial: 'As a frequent business traveler, I\'ve stayed in countless serviced apartments worldwide. Proximus stands out for its attention to detail and genuine hospitality. The apartment was immaculate, the kitchen fully equipped for my dietary needs, and the location perfect for accessing both corporate offices and the airport. The staff remembered my preferences from my first stay and had everything ready for my return visits. This is now my permanent Bangalore home base.',
    stayDuration: '6 Weeks',
    apartmentType: 'Luxos Luxury',
    date: 'October 2024',
    guestType: 'business'
  },
  {
    id: 3,
    name: 'Priya Sharma',
    role: 'Project Manager',
    company: 'Enterprise Solutions Inc',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14b3a32aa-1772054739413.png",
    alt: 'Young Indian professional woman in blue blazer with warm smile in modern workspace',
    rating: 5,
    testimonial: 'Relocating to Bangalore for a year-long project was daunting until I found Proximus. The Classica Comfort apartment provided everything I needed—spacious living area, fully equipped kitchen, and a dedicated workspace. The building amenities like the gym and rooftop lounge became my daily routine. The neighborhood guide they provided helped me discover amazing local restaurants and shops. It truly felt like home away from home.',
    stayDuration: '12 Months',
    apartmentType: 'Classica Comfort',
    date: 'September 2024',
    guestType: 'extended'
  },
  {
    id: 4,
    name: 'Michael Chen',
    role: 'Chief Technology Officer',
    company: 'Innovation Labs',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1de8d0400-1763294237371.png",
    alt: 'Asian male executive in charcoal suit with glasses in professional office environment',
    rating: 5,
    testimonial: 'Proximus transformed our corporate housing experience. We house 15+ executives here monthly, and the consistency of quality is remarkable. The corporate partnership program streamlined our booking process, and the dedicated account manager ensures every detail is perfect. The apartments are modern, secure, and perfectly located near our Bangalore office. Our team productivity has improved significantly with such comfortable accommodations.',
    stayDuration: 'Ongoing Partnership',
    apartmentType: 'Multiple Units',
    date: 'August 2024',
    guestType: 'corporate'
  },
  {
    id: 5,
    name: 'Anita Desai',
    role: 'Family Traveler',
    company: 'Independent',
    image: "https://images.unsplash.com/photo-1652396944757-ad27b62b33f6",
    alt: 'Indian woman with long dark hair in casual elegant attire smiling warmly outdoors',
    rating: 5,
    testimonial: 'Our family vacation to Bangalore was made perfect by Proximus. The two-bedroom Classica apartment gave us the space we needed, unlike cramped hotel rooms. The kids loved the pool, and we enjoyed cooking some meals in the full kitchen. The staff arranged a car for our day trips and recommended family-friendly restaurants. The location was ideal for exploring both the tech parks and cultural sites. We\'ll definitely return!',
    stayDuration: '2 Weeks',
    apartmentType: 'Classica Comfort',
    date: 'December 2024',
    guestType: 'leisure'
  },
  {
    id: 6,
    name: 'David Thompson',
    role: 'Senior Consultant',
    company: 'Strategy Partners',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14fb66cbd-1778490108091.png",
    alt: 'Caucasian male professional in white shirt with friendly expression in bright office',
    rating: 5,
    testimonial: 'The level of service at Proximus is unmatched. From the airport pickup to the personalized welcome package, every touchpoint showed genuine care. The apartment\'s smart home features, premium bedding, and rainfall shower made it feel luxurious. The business center was perfect for client meetings, and the rooftop workspace offered inspiring views. The staff\'s local knowledge helped me discover Bangalore\'s hidden gems. Exceptional experience!',
    stayDuration: '8 Weeks',
    apartmentType: 'Luxos Luxury',
    date: 'November 2024',
    guestType: 'business'
  }];


  const videoTestimonials: VideoTestimonialData[] = [
  {
    id: 1,
    name: 'Amit Patel',
    role: 'CEO, Tech Startup',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_11ba40b81-1772451931587.png",
    alt: 'Modern luxury apartment living room with floor-to-ceiling windows and city skyline view',
    duration: '2:45',
    title: 'Why Proximus is Perfect for Business Travelers'
  },
  {
    id: 2,
    name: 'Jennifer Lee',
    role: 'HR Director, Global Corp',
    thumbnail: "https://images.unsplash.com/photo-1627670380526-82405ef9d509",
    alt: 'Elegant serviced apartment interior with contemporary furniture and warm lighting',
    duration: '3:12',
    title: 'Corporate Housing Solution That Works'
  },
  {
    id: 3,
    name: 'Vikram Singh',
    role: 'Consultant, Big Four Firm',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_13949de0c-1772132425092.png",
    alt: 'Spacious modern bedroom with king-size bed and minimalist design in luxury apartment',
    duration: '2:30',
    title: 'Extended Stay Comfort at Proximus'
  },
  {
    id: 4,
    name: 'Emma Wilson',
    role: 'Travel Blogger',
    thumbnail: "https://images.unsplash.com/photo-1722604817803-4c88edef9bc0",
    alt: 'Fully equipped modern kitchen with marble countertops and stainless steel appliances',
    duration: '4:05',
    title: 'Family-Friendly Serviced Apartments Review'
  }];


  const caseStudies: CaseStudy[] = [
  {
    id: 1,
    title: 'Tech Giant\'s 6-Month Project Housing',
    category: 'Corporate Partnership',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b93a64c-1771894463737.png",
    alt: 'Modern corporate office space with multiple professionals collaborating around conference table',
    challenge: 'A leading technology company needed to house 25 senior executives for a 6-month digital transformation project in Bangalore. They required consistent quality, flexible terms, and proximity to their office campus.',
    solution: 'Proximus created a customized corporate package with dedicated account management, priority booking for multiple Luxos Luxury units, and flexible check-in/check-out schedules. We provided a single point of contact for all 25 guests and arranged weekly housekeeping and laundry services.',
    outcome: 'The project team reported 95% satisfaction rates, with executives praising the home-like comfort and professional amenities. The company has since established an ongoing partnership with Proximus for all Bangalore-based projects, housing 100+ executives annually.',
    guestName: 'Lisa Anderson',
    guestRole: 'VP of Operations, Tech Giant Inc',
    guestType: 'corporate'
  },
  {
    id: 2,
    title: 'Consultant\'s Year-Long Relocation Success',
    category: 'Extended Stay',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd95a5e9-1773000484794.png",
    alt: 'Professional businessman working on laptop in modern apartment workspace with natural lighting',
    challenge: 'A senior management consultant needed accommodation for a 12-month assignment in Bangalore. He required a professional workspace, proximity to IIM Bangalore, and amenities that would make extended stay comfortable without feeling isolated.',
    solution: 'We provided a Luxos Luxury apartment with a dedicated home office setup, ergonomic furniture, and high-speed fiber internet. Our concierge team arranged weekly grocery deliveries, connected him with local business networks, and organized monthly social events for long-term residents.',
    outcome: 'The consultant successfully completed his project while maintaining work-life balance. He extended his stay by 3 months and has since referred 8 colleagues to Proximus. He credits the comfortable living environment and community connections for his project success.',
    guestName: 'Rahul Mehta',
    guestRole: 'Senior Consultant, Global Advisory Firm',
    guestType: 'extended'
  },
  {
    id: 3,
    title: 'Family\'s Seamless Bangalore Exploration',
    category: 'Leisure Travel',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9d52b39-1772306938538.png",
    alt: 'Happy family of four relaxing together in spacious living room with comfortable seating',
    challenge: 'A family of four from Singapore wanted to explore Bangalore for 3 weeks but found traditional hotels too cramped and expensive. They needed separate sleeping areas for children, a kitchen for dietary preferences, and family-friendly amenities.',
    solution: 'Proximus offered a two-bedroom Classica Comfort apartment with a full kitchen, living area, and access to the pool and play area. We provided a customized family guide to Bangalore, arranged car rentals, and recommended child-friendly restaurants and attractions.',
    outcome: 'The family enjoyed a memorable vacation with the space and flexibility they needed. They cooked breakfast daily, did laundry in-unit, and used the apartment as a comfortable base for daily explorations. They\'ve booked their next India trip with Proximus and recommended us to their travel community.',
    guestName: 'The Tan Family',
    guestRole: 'Leisure Travelers from Singapore',
    guestType: 'leisure'
  }];


  const handleFilterChange = (filters: FilterState) => {
    let filtered = testimonials;
    let filteredCases = caseStudies;

    if (filters.guestType !== 'all') {
      filtered = filtered.filter((t) => t.guestType === filters.guestType);
      filteredCases = filteredCases.filter((c) => c.guestType === filters.guestType);
    }

    if (filters.apartmentType !== 'all') {
      filtered = filtered.filter((t) =>
      t.apartmentType.toLowerCase().includes(filters.apartmentType)
      );
    }

    if (filters.rating !== 'all') {
      const minRating = parseInt(filters.rating);
      filtered = filtered.filter((t) => t.rating >= minRating);
    }

    setFilteredData({
      testimonials: filtered,
      caseStudies: filteredCases
    });
  };

  const displayTestimonials = filteredData?.testimonials || testimonials;
  const displayCaseStudies = filteredData?.caseStudies || caseStudies;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex flex-wrap gap-4 mb-8 border-b border-border">
        <button
          onClick={() => setActiveTab('testimonials')}
          className={`px-6 py-3 font-semibold transition-all duration-300 border-b-2 ${
          activeTab === 'testimonials' ? 'border-accent text-accent' : 'border-transparent text-text-secondary hover:text-primary'}`
          }>

          <div className="flex items-center space-x-2">
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
            <span>Guest Testimonials</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('videos')}
          className={`px-6 py-3 font-semibold transition-all duration-300 border-b-2 ${
          activeTab === 'videos' ? 'border-accent text-accent' : 'border-transparent text-text-secondary hover:text-primary'}`
          }>

          <div className="flex items-center space-x-2">
            <Icon name="VideoCameraIcon" size={20} />
            <span>Video Stories</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('case-studies')}
          className={`px-6 py-3 font-semibold transition-all duration-300 border-b-2 ${
          activeTab === 'case-studies' ? 'border-accent text-accent' : 'border-transparent text-text-secondary hover:text-primary'}`
          }>

          <div className="flex items-center space-x-2">
            <Icon name="DocumentTextIcon" size={20} />
            <span>Case Studies</span>
          </div>
        </button>
      </div>

      {(activeTab === 'testimonials' || activeTab === 'case-studies') &&
      <FilterBar onFilterChange={handleFilterChange} />
      }

      {activeTab === 'testimonials' &&
      <div className="grid md:grid-cols-2 gap-6">
          {displayTestimonials.map((testimonial) =>
        <TestimonialCard key={testimonial.id} {...testimonial} />
        )}
        </div>
      }

      {activeTab === 'videos' &&
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {videoTestimonials.map((video) =>
        <VideoTestimonial key={video.id} {...video} />
        )}
        </div>
      }

      {activeTab === 'case-studies' &&
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayCaseStudies.map((caseStudy) =>
        <CaseStudyCard key={caseStudy.id} {...caseStudy} />
        )}
        </div>
      }

      {(activeTab === 'testimonials' && displayTestimonials.length === 0 ||
      activeTab === 'case-studies' && displayCaseStudies.length === 0) &&
      <div className="text-center py-16">
          <Icon name="FaceFrownIcon" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary text-lg">
            No stories match your current filters. Try adjusting your selection.
          </p>
        </div>
      }
    </div>);

}